import { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, onSnapshot, where, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';

export interface SavedComparisonGroup {
  id?: string;
  name: string;
  description?: string;
  products: any[]; // Array de productos guardados en el grupo
  isPublic: boolean;
  createdBy: string;
  createdByName: string;
  clientId?: string; // ID del cliente asociado (opcional para compatibilidad)
  createdAt: Timestamp;
  updatedAt: Timestamp;
  productCount: number; // Número de productos en el grupo
}

// TEMPORALMENTE DESHABILITADO - Evitar errores críticos de Firestore
const GROUPS_FEATURE_DISABLED = false;

export const useSavedComparisonGroups = () => {
  const [savedGroups, setSavedGroups] = useState<SavedComparisonGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(GROUPS_FEATURE_DISABLED);
  const { currentUser, userData } = useAuth();
  const { selectedClient } = useClient();

  // Cargar grupos guardados
  useEffect(() => {
    // Si está deshabilitado, no hacer nada
    if (GROUPS_FEATURE_DISABLED) {
      setLoading(false);
      setSavedGroups([]);
      setDisabled(true);
      return;
    }

    // Si no hay cliente seleccionado, no cargar grupos
    if (!selectedClient) {
      setSavedGroups([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Flag para evitar actualizaciones después de cleanup
    let isSubscribed = true;

    const groupsRef = collection(db, 'savedComparisonGroups');
    let publicGroups: SavedComparisonGroup[] = [];
    let privateGroups: SavedComparisonGroup[] = [];
    let publicLoaded = false;
    let privateLoaded = false;
    let unsubscribePublic: (() => void) | null = null;
    let unsubscribePrivate: (() => void) | null = null;

    const updateCombinedGroups = () => {
      if (!isSubscribed) return; // No actualizar si ya se hizo cleanup

      if (publicLoaded && (privateLoaded || !currentUser)) {
        const allGroups = [...publicGroups, ...privateGroups];
        setSavedGroups(allGroups);
        setLoading(false);
      }
    };

    // Usar setTimeout para evitar race conditions con Firestore
    const setupListeners = () => {
      if (!isSubscribed) return;

      // Query para grupos públicos
      const publicQuery = query(
        groupsRef,
        where('isPublic', '==', true)
      );

      unsubscribePublic = onSnapshot(publicQuery, (snapshot) => {
        if (!isSubscribed) return;

        publicGroups = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as SavedComparisonGroup))
        .filter(g => {
          if (!g.clientId) return false;
          return g.clientId === selectedClient.clientId;
        })
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(0);
          return dateB.getTime() - dateA.getTime();
        });

        publicLoaded = true;
        updateCombinedGroups();
      }, (err) => {
        if (!isSubscribed) return;
        console.error('Error loading public groups:', err);
        setError('Error al cargar los grupos públicos');
        publicLoaded = true;
        updateCombinedGroups();
      });

      // Query para grupos privados (solo si hay usuario)
      if (currentUser) {
        const privateQuery = query(
          groupsRef,
          where('isPublic', '==', false),
          where('createdBy', '==', currentUser.uid)
        );

        unsubscribePrivate = onSnapshot(privateQuery, (privateSnapshot) => {
          if (!isSubscribed) return;

          privateGroups = privateSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as SavedComparisonGroup))
          .filter(g => {
            if (!g.clientId) return true;
            return g.clientId === selectedClient.clientId;
          })
          .sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(0);
            const dateB = b.createdAt?.toDate?.() || new Date(0);
            return dateB.getTime() - dateA.getTime();
          });

          privateLoaded = true;
          updateCombinedGroups();
        }, (err) => {
          if (!isSubscribed) return;
          console.error('Error loading private groups:', err);
          privateLoaded = true;
          updateCombinedGroups();
        });
      } else {
        privateLoaded = true;
        updateCombinedGroups();
      }
    };

    // Pequeño delay para evitar problemas de inicialización de Firestore
    const timeoutId = setTimeout(setupListeners, 100);

    return () => {
      isSubscribed = false;
      clearTimeout(timeoutId);
      if (unsubscribePublic) {
        unsubscribePublic();
      }
      if (unsubscribePrivate) {
        unsubscribePrivate();
      }
    };
  }, [currentUser, selectedClient]);

  // Guardar nuevo grupo de comparación
  const saveComparisonGroup = async (
    name: string,
    products: any[],
    isPublic: boolean = true,
    description?: string
  ): Promise<string> => {
    if (GROUPS_FEATURE_DISABLED) {
      throw new Error('Funcionalidad de grupos temporalmente deshabilitada');
    }
    
    if (!currentUser) {
      throw new Error('Usuario no autenticado');
    }

    if (!userData) {
      throw new Error('Datos de usuario no disponibles');
    }

    if (!selectedClient) {
      throw new Error('No hay cliente seleccionado');
    }

    if (!products || products.length === 0) {
      throw new Error('Debe incluir al menos un producto en el grupo');
    }

    // Limpia recursivamente valores undefined para cumplir con restricciones de Firestore
    const cleanUndefinedDeep = (value: any): any => {
      if (Array.isArray(value)) {
        return value.map(item => cleanUndefinedDeep(item));
      }
      if (value && typeof value === 'object') {
        return Object.entries(value).reduce((acc: Record<string, any>, [k, v]) => {
          if (v !== undefined) {
            acc[k] = cleanUndefinedDeep(v);
          }
          return acc;
        }, {});
      }
      return value;
    };

    const cleanedProducts = cleanUndefinedDeep(products);

    const groupData: Omit<SavedComparisonGroup, 'id'> = {
      name,
      products: cleanedProducts,
      productCount: cleanedProducts.length,
      isPublic,
      createdBy: currentUser.uid,
      createdByName: userData.name || currentUser.email || 'Usuario',
      clientId: selectedClient.clientId, // Asociar al cliente seleccionado
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      // Solo incluir description si tiene un valor válido
      ...(description && description.trim() ? { description: description.trim() } : {})
    };

    try {
      const docRef = await addDoc(collection(db, 'savedComparisonGroups'), groupData);
      return docRef.id;
    } catch (err) {
      
      throw new Error(`Error al guardar el grupo de comparación: ${err instanceof Error ? err.message : 'Error desconocido'}`);
    }
  };

  // Actualizar grupo existente
  const updateComparisonGroup = async (
    groupId: string,
    updates: Partial<Pick<SavedComparisonGroup, 'name' | 'description' | 'products' | 'isPublic'>>
  ): Promise<void> => {
    if (GROUPS_FEATURE_DISABLED) {
      throw new Error('Funcionalidad de grupos temporalmente deshabilitada');
    }
    
    if (!currentUser) {
      throw new Error('Usuario no autenticado');
    }

    const group = savedGroups.find(g => g.id === groupId);
    if (!group) {
      throw new Error('Grupo no encontrado');
    }

    // Permitir editar si es el propietario o si es un grupo público
    if (group.createdBy !== currentUser.uid && !group.isPublic) {
      throw new Error('No tienes permisos para modificar este grupo');
    }

    try {
      const groupRef = doc(db, 'savedComparisonGroups', groupId);
      
      // Limpiar campos undefined de las actualizaciones
      const cleanUndefinedDeep = (value: any): any => {
        if (Array.isArray(value)) {
          return value.map(item => cleanUndefinedDeep(item));
        }
        if (value && typeof value === 'object') {
          return Object.entries(value).reduce((acc: Record<string, any>, [k, v]) => {
            if (v !== undefined) {
              acc[k] = cleanUndefinedDeep(v);
            }
            return acc;
          }, {});
        }
        return value;
      };

      const cleanUpdates = Object.entries(updates).reduce((acc, [key, value]) => {
        if (key === 'description') {
          // Solo incluir description si tiene un valor válido
          if (value && typeof value === 'string' && value.trim()) {
            acc[key] = value.trim();
          }
        } else if (key === 'products' && Array.isArray(value)) {
          const cleanedProducts = cleanUndefinedDeep(value);
          acc[key] = cleanedProducts;
          acc['productCount'] = cleanedProducts.length; // Actualizar el conteo
        } else if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
      
      await updateDoc(groupRef, {
        ...cleanUpdates,
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      
      throw new Error('Error al actualizar el grupo de comparación');
    }
  };

  // Eliminar grupo
  const deleteComparisonGroup = async (groupId: string): Promise<void> => {
    if (GROUPS_FEATURE_DISABLED) {
      throw new Error('Funcionalidad de grupos temporalmente deshabilitada');
    }
    
    if (!currentUser) {
      throw new Error('Usuario no autenticado');
    }

    const group = savedGroups.find(g => g.id === groupId);
    if (!group) {
      throw new Error('Grupo no encontrado');
    }

    if (group.createdBy !== currentUser.uid) {
      throw new Error('No tienes permisos para eliminar este grupo');
    }

    try {
      await deleteDoc(doc(db, 'savedComparisonGroups', groupId));
    } catch (err) {
      
      throw new Error('Error al eliminar el grupo de comparación');
    }
  };

  // Cargar productos de un grupo específico
  const loadComparisonGroup = (groupId: string): any[] => {
    
    if (GROUPS_FEATURE_DISABLED) {
      return [];
    }
    
    const group = savedGroups.find(g => g.id === groupId);
    
    if (group) {
      
      // Verificar que products existe y es un array
      if (!group.products) {
        console.warn('⚠️ Group has no products property');
        return [];
      }
      
      if (!Array.isArray(group.products)) {
        console.warn('⚠️ Group products is not an array:', typeof group.products);
        return [];
      }
      
      // Filtrar productos válidos (no null, no undefined)
      const validProducts = group.products.filter(p => p != null);
      
      return validProducts;
    } else {
      console.warn('⚠️ Group not found with id:', groupId);
      return [];
    }
  };

  // Verificar si los productos actuales coinciden con algún grupo guardado
  const findMatchingGroup = (currentProducts: any[]): SavedComparisonGroup | null => {
    if (GROUPS_FEATURE_DISABLED || !currentProducts || currentProducts.length === 0) {
      return null;
    }

    // Extraer IDs de productos actuales
    const currentProductIds = currentProducts.map(product => 
      product.comparisonId || product.id_catalogo || product.ID_CATALOGO || product.id
    ).filter(id => id !== undefined).sort();

    return savedGroups.find(group => {
      if (!group.products || group.products.length !== currentProducts.length) return false;
      
      // Extraer IDs de productos del grupo guardado
      const groupProductIds = group.products.map(product => 
        product.comparisonId || product.id_catalogo || product.ID_CATALOGO || product.id
      ).filter(id => id !== undefined).sort();

      // Comparar arrays de IDs
      return JSON.stringify(currentProductIds) === JSON.stringify(groupProductIds);
    }) || null;
  };

  return {
    savedGroups,
    loading,
    error,
    disabled,
    saveComparisonGroup,
    updateComparisonGroup,
    deleteComparisonGroup,
    loadComparisonGroup,
    findMatchingGroup
  };
}; 